import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import API from "../services/api";
import useI18n from "../i18n/useI18n";
import LibrarySkeleton from "./loaders/LibrarySkeleton";

const THEME_KEY = "hookora_theme";

const TOOL_TYPE_ALIASES = {
  hooks: "hooks",
  hook_generator: "hooks",
  viral_hooks: "hooks",
  viral_hook_generator: "hooks",

  youtube_title_optimizer: "youtube_title_optimizer",
  youtube_titles: "youtube_title_optimizer",
  youtube_title: "youtube_title_optimizer",

  caption_generator: "caption_generator",
  captions: "caption_generator",
  caption: "caption_generator",

  hashtag_generator: "hashtag_generator",
  hashtags: "hashtag_generator",
  hashtag: "hashtag_generator",

  description_rewriter: "description_rewriter",
  description_rewrite: "description_rewriter",
  descriptions: "description_rewriter",
  description: "description_rewriter",

  short_script_generator: "short_script_generator",
  short_scripts: "short_script_generator",
  short_script: "short_script_generator",

  thumbnail_text_analyzer: "thumbnail_text_analyzer",
  thumbnail_text: "thumbnail_text_analyzer",
  thumbnail_texts: "thumbnail_text_analyzer",

  hook_angles: "hook_angles",
  hook_angle: "hook_angles",

  cta_generator: "cta_generator",
  ctas: "cta_generator",
  cta: "cta_generator",

  humanizer: "humanizer",
  ai_humanizer: "humanizer",
  ai_humanizer_tool: "humanizer",
  humanized_rewrite: "humanizer",
  humanized_rewrites: "humanizer"
};

const TOOL_META = {
  hooks: {
    labelKey: "libraryPage.tools.hooks.label",
    badgeKey: "libraryPage.tools.hooks.badge"
  },
  youtube_title_optimizer: {
    labelKey: "libraryPage.tools.youtubeTitleOptimizer.label",
    badgeKey: "libraryPage.tools.youtubeTitleOptimizer.badge"
  },
  caption_generator: {
    labelKey: "libraryPage.tools.captionGenerator.label",
    badgeKey: "libraryPage.tools.captionGenerator.badge"
  },
  hashtag_generator: {
    labelKey: "libraryPage.tools.hashtagGenerator.label",
    badgeKey: "libraryPage.tools.hashtagGenerator.badge"
  },
  description_rewriter: {
    labelKey: "libraryPage.tools.descriptionRewriter.label",
    badgeKey: "libraryPage.tools.descriptionRewriter.badge"
  },
  short_script_generator: {
    labelKey: "libraryPage.tools.shortScriptGenerator.label",
    badgeKey: "libraryPage.tools.shortScriptGenerator.badge"
  },
  thumbnail_text_analyzer: {
    labelKey: "libraryPage.tools.thumbnailTextAnalyzer.label",
    badgeKey: "libraryPage.tools.thumbnailTextAnalyzer.badge"
  },
  hook_angles: {
    labelKey: "libraryPage.tools.hookAngles.label",
    badgeKey: "libraryPage.tools.hookAngles.badge"
  },
  cta_generator: {
    labelKey: "libraryPage.tools.ctaGenerator.label",
    badgeKey: "libraryPage.tools.ctaGenerator.badge"
  },
  humanizer: {
    labelKey: "libraryPage.tools.humanizer.label",
    badgeKey: "libraryPage.tools.humanizer.badge"
  },
  other: {
    labelKey: "libraryPage.tools.other.label",
    badgeKey: "libraryPage.tools.other.badge"
  }
};

const normalizeToolType = (value) => {
  if (!value) return "other";
  const normalized = String(value).trim().toLowerCase().replace(/-/g, "_").replace(/\s+/g, "_");
  return TOOL_TYPE_ALIASES[normalized] || (TOOL_META[normalized] ? normalized : "other");
};

const getToolMeta = (toolType, fallbackLabel, fallbackBadge, t) => {
  const normalized = normalizeToolType(toolType);
  const defaultMeta = TOOL_META[normalized] || TOOL_META.other;

  return {
    toolType: normalized,
    label: fallbackLabel || t(defaultMeta.labelKey),
    badge: fallbackBadge || t(defaultMeta.badgeKey)
  };
};

const HookLibrary = () => {
  const { t } = useI18n();

  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [hooks, setHooks] = useState([]);
  const [studioAssets, setStudioAssets] = useState([]);
  const [folderNames, setFolderNames] = useState({});
  const [editingFolderKey, setEditingFolderKey] = useState("");
  const [editingFolderValue, setEditingFolderValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copiedHookId, setCopiedHookId] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [selectedHookIds, setSelectedHookIds] = useState([]);
  const [currentPlan, setCurrentPlan] = useState("free");
  const [theme, setTheme] = useState(getStoredTheme());
  const [openFolders, setOpenFolders] = useState({});

  const [searchQuery, setSearchQuery] = useState("");
  const [platformFilter, setPlatformFilter] = useState("All");
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [toolFilter, setToolFilter] = useState("All");
  const [sortBy, setSortBy] = useState("newest");
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  const isCreatorOrHigher = currentPlan === "creator" || currentPlan === "pro";
  const isPro = currentPlan === "pro";
  const isDark = theme === "dark";

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const colors = useMemo(
    () => ({
      pageBg: isDark ? "#020617" : "#f8fafc",
      pageGradient: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
        : "none",
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      cardBgStrong: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
      cardSubtle: isDark ? "rgba(30,41,59,0.68)" : "#f8fafc",
      border: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.28)" : "#d1d5db",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#374151",
      shadow: isDark
        ? "0 14px 40px rgba(2, 6, 23, 0.45)"
        : "0 8px 24px rgba(15, 23, 42, 0.04)",
      shadowSoft: isDark
        ? "0 10px 26px rgba(2, 6, 23, 0.34)"
        : "0 4px 14px rgba(15, 23, 42, 0.03)",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
      trackBg: isDark ? "#1e293b" : "#e5e7eb",
      chipBg: isDark ? "rgba(30,41,59,0.9)" : "#f1f5f9",
      chipText: isDark ? "#cbd5e1" : "#475569",
      favoriteBg: isDark ? "rgba(245,158,11,0.18)" : "#fef3c7",
      favoriteText: isDark ? "#fbbf24" : "#92400e",
      lockedBg: isDark ? "rgba(30,41,59,0.75)" : "#f8fafc",
      lockedBorder: isDark ? "rgba(148,163,184,0.28)" : "#94a3b8",
      lockedText: isDark ? "#cbd5e1" : "#475569",
      warningBg: isDark ? "rgba(120,53,15,0.24)" : "#fff7ed",
      warningBorder: isDark ? "rgba(251,146,60,0.34)" : "#fdba74",
      warningText: isDark ? "#fdba74" : "#9a3412",
      purpleBg: isDark ? "rgba(88,28,135,0.18)" : "#faf5ff",
      purpleBorder: isDark ? "rgba(216,180,254,0.34)" : "#d8b4fe",
      purpleText: isDark ? "#e9d5ff" : "#6b21a8",
      softWarningBg: isDark ? "rgba(120,53,15,0.24)" : "#fff7ed",
      softWarningBorder: isDark ? "rgba(251,146,60,0.34)" : "#fdba74",
      softWarningText: isDark ? "#fdba74" : "#9a3412",
      linkButtonBg: isDark ? "#e2e8f0" : "#111827",
      linkButtonText: isDark ? "#020617" : "#ffffff",
      folderBg: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
      folderHeaderBg: isDark ? "rgba(30,41,59,0.75)" : "#f8fafc",
      accentBlueBg: isDark ? "rgba(37,99,235,0.18)" : "#dbeafe",
      accentBlueText: isDark ? "#bfdbfe" : "#1d4ed8",
      accentBlueBorder: isDark ? "rgba(147,197,253,0.28)" : "#93c5fd",
      accentSlateBg: isDark ? "rgba(51,65,85,0.55)" : "#f1f5f9",
      accentSlateText: isDark ? "#cbd5e1" : "#334155",
      accentSlateBorder: isDark ? "rgba(148,163,184,0.22)" : "#cbd5e1"
    }),
    [isDark]
  );

  const inputStyle = {
    padding: "10px 12px",
    borderRadius: "10px",
    border: `1px solid ${colors.borderStrong}`,
    width: "100%",
    boxSizing: "border-box",
    background: colors.inputBg,
    color: colors.inputText
  };

  const statCardStyle = {
    padding: "22px",
    border: `1px solid ${colors.border}`,
    borderRadius: "18px",
    background: colors.cardBg,
    boxShadow: colors.shadow,
    backdropFilter: isDark ? "blur(14px)" : "none"
  };

  const getBadgeStyle = useCallback(
    (toolType) => {
      const normalized = normalizeToolType(toolType);

      if (normalized === "hook_angles") {
        return {
          background: isDark ? "rgba(16,185,129,0.18)" : "#d1fae5",
          color: isDark ? "#a7f3d0" : "#065f46",
          border: `1px solid ${isDark ? "rgba(110,231,183,0.28)" : "#86efac"}`
        };
      }

      if (normalized === "cta_generator") {
        return {
          background: isDark ? "rgba(124,58,237,0.18)" : "#ede9fe",
          color: isDark ? "#ddd6fe" : "#6d28d9",
          border: `1px solid ${isDark ? "rgba(196,181,253,0.28)" : "#c4b5fd"}`
        };
      }

      if (normalized === "humanizer") {
        return {
          background: isDark ? "rgba(245,158,11,0.18)" : "#fef3c7",
          color: isDark ? "#fde68a" : "#92400e",
          border: `1px solid ${isDark ? "rgba(252,211,77,0.28)" : "#fcd34d"}`
        };
      }

      return {
        background: colors.accentBlueBg,
        color: colors.accentBlueText,
        border: `1px solid ${colors.accentBlueBorder}`
      };
    },
    [colors, isDark]
  );

  const normalizeStudioAsset = useCallback(
    (asset) => {
      const meta = getToolMeta(asset?.tool_type, asset?.tool_label, asset?.tool_badge, t);

      return {
        ...asset,
        tool_type: meta.toolType,
        tool_label: meta.label,
        tool_badge: meta.badge
      };
    },
    [t]
  );

  const loadLibrary = useCallback(async () => {
    try {
      setLoading(true);

      const [hooksRes, profileRes, assetsRes, foldersRes] = await Promise.all([
        API.get("/hooks/history"),
        API.get("/users/profile"),
        API.get("/studio-assets/"),
        API.get("/library-folders/").catch(() => ({ data: [] }))
      ]);

      setHooks(hooksRes.data || []);
      setStudioAssets((assetsRes.data || []).map(normalizeStudioAsset));
      setCurrentPlan(profileRes.data?.plan || "free");

      const namesMap = {};
      (foldersRes.data || []).forEach((folder) => {
        namesMap[folder.folder_key] = folder.display_name;
      });
      setFolderNames(namesMap);

      setError("");
    } catch (err) {
      console.error("Failed to load library:", err);
      setError(
        err?.response?.status === 401
          ? t("libraryPage.messages.notLoggedIn")
          : t("libraryPage.messages.loadFailed")
      );
    } finally {
      setLoading(false);
    }
  }, [normalizeStudioAsset, t]);

  useEffect(() => {
    loadLibrary();
  }, [loadLibrary]);

  const showUpgradePrompt = (featureName) => {
    setError(t("libraryPage.messages.upgradeRequired", { featureName }));
  };

  const copyHook = async (hookText, hookId) => {
    try {
      await navigator.clipboard.writeText(hookText);
      setCopiedHookId(hookId);
      setTimeout(() => setCopiedHookId(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const toggleFavorite = async (hookId) => {
    try {
      setActionLoadingId(hookId);
      await API.patch(`/hooks/${hookId}/favorite`);
      await loadLibrary();
    } catch (err) {
      console.error("Failed to toggle favorite:", err);
      setError(err?.response?.data?.detail || t("libraryPage.messages.favoriteUpdateFailed"));
    } finally {
      setActionLoadingId(null);
    }
  };

  const deleteHook = async (hookId) => {
    const shouldDelete = window.confirm(t("libraryPage.confirmations.deleteHook"));
    if (!shouldDelete) return;

    try {
      setActionLoadingId(hookId);
      await API.delete(`/hooks/${hookId}`);
      setSelectedHookIds((prev) => prev.filter((id) => id !== hookId));
      await loadLibrary();
    } catch (err) {
      console.error("Failed to delete hook:", err);
      setError(err?.response?.data?.detail || t("libraryPage.messages.deleteHookFailed"));
    } finally {
      setActionLoadingId(null);
    }
  };

  const deleteStudioAsset = async (assetId) => {
    const shouldDelete = window.confirm(t("libraryPage.confirmations.deleteAsset"));
    if (!shouldDelete) return;

    try {
      setActionLoadingId(`asset-${assetId}`);
      await API.delete(`/studio-assets/${assetId}`);
      await loadLibrary();
    } catch (err) {
      console.error("Failed to delete studio asset:", err);
      setError(err?.response?.data?.detail || t("libraryPage.messages.deleteAssetFailed"));
    } finally {
      setActionLoadingId(null);
    }
  };

  const deleteFolder = async (folderKey, folder) => {
    const totalItems = folder.hooks.length + folder.assets.length;
    const shouldDelete = window.confirm(
      t("libraryPage.confirmations.deleteFolder", {
        folderName: folder.label,
        itemCount: totalItems
      })
    );
    if (!shouldDelete) return;

    try {
      setActionLoadingId(`folder-${folderKey}`);

      if (folder.hooks.length) {
        await API.post("/hooks/bulk/delete", {
          hook_ids: folder.hooks.map((hook) => hook.id)
        });
      }

      if (folder.assets.length) {
        await Promise.all(folder.assets.map((asset) => API.delete(`/studio-assets/${asset.id}`)));
      }

      await API.delete(`/library-folders/${encodeURIComponent(folderKey)}`).catch(() => null);

      setSelectedHookIds((prev) =>
        prev.filter((id) => !folder.hooks.some((hook) => hook.id === id))
      );

      await loadLibrary();
    } catch (err) {
      console.error("Failed to delete folder:", err);
      setError(err?.response?.data?.detail || t("libraryPage.messages.deleteFolderFailed"));
    } finally {
      setActionLoadingId(null);
    }
  };

  const regenerateHook = async (hookId) => {
    try {
      setActionLoadingId(hookId);
      await API.post(`/hooks/${hookId}/regenerate`, {
        template: "curiosity"
      });
      await loadLibrary();
    } catch (err) {
      console.error("Failed to regenerate hook:", err);
      setError(err?.response?.data?.detail || t("libraryPage.messages.regenerateFailed"));
    } finally {
      setActionLoadingId(null);
    }
  };

  const renderScoreBar = (score) => {
    const safeScore = Number(score) || 0;
    const color = safeScore >= 80 ? "#22c55e" : safeScore >= 50 ? "#f59e0b" : "#ef4444";

    return (
      <div
        style={{
          height: "10px",
          background: colors.trackBg,
          borderRadius: "999px",
          marginTop: "8px",
          overflow: "hidden"
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, safeScore))}%`,
            height: "100%",
            backgroundColor: color,
            borderRadius: "999px",
            transition: "width 0.3s ease"
          }}
        />
      </div>
    );
  };

  const formatPlanLabel = (plan) => {
    const normalized = (plan || "free").toLowerCase();
    if (normalized === "creator") return t("libraryPage.planLabels.creator");
    if (normalized === "pro") return t("libraryPage.planLabels.pro");
    return t("libraryPage.planLabels.free");
  };

  const getPlanBadgeStyle = (plan) => {
    const normalized = (plan || "free").toLowerCase();

    if (normalized === "creator") {
      return {
        color: "#1d4ed8",
        background: isDark ? "rgba(29,78,216,0.18)" : "#dbeafe",
        border: `1px solid ${isDark ? "rgba(147,197,253,0.34)" : "#93c5fd"}`
      };
    }

    if (normalized === "pro") {
      return {
        color: "#8b5cf6",
        background: isDark ? "rgba(109,40,217,0.18)" : "#f3e8ff",
        border: `1px solid ${isDark ? "rgba(216,180,254,0.34)" : "#d8b4fe"}`
      };
    }

    return {
      color: isDark ? "#fbbf24" : "#92400e",
      background: isDark ? "rgba(245,158,11,0.18)" : "#fef3c7",
      border: `1px solid ${isDark ? "rgba(252,211,77,0.28)" : "#fcd34d"}`
    };
  };

  const filteredHooks = useMemo(() => {
    let result = [...hooks];

    if (platformFilter !== "All") {
      result = result.filter((hook) => hook.platform === platformFilter);
    }

    if (favoritesOnly) {
      result = result.filter((hook) => hook.is_favorite);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (hook) =>
          hook.topic.toLowerCase().includes(query) ||
          hook.content.toLowerCase().includes(query) ||
          hook.platform.toLowerCase().includes(query)
      );
    }

    if (sortBy === "newest") {
      result.sort(
        (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
      );
    } else if (sortBy === "oldest") {
      result.sort(
        (a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
      );
    } else if (sortBy === "score-high") {
      result.sort((a, b) => b.score - a.score);
    } else if (sortBy === "score-low") {
      result.sort((a, b) => a.score - b.score);
    } else if (sortBy === "topic") {
      result.sort((a, b) => a.topic.localeCompare(b.topic));
    }

    return result;
  }, [hooks, platformFilter, favoritesOnly, searchQuery, sortBy]);

  const filteredStudioAssets = useMemo(() => {
    let result = [...studioAssets];

    if (platformFilter !== "All") {
      result = result.filter((asset) => (asset.platform || "") === platformFilter);
    }

    if (toolFilter !== "All") {
      result = result.filter((asset) => normalizeToolType(asset.tool_type) === toolFilter);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (asset) =>
          (asset.topic || "").toLowerCase().includes(query) ||
          (asset.title || "").toLowerCase().includes(query) ||
          (asset.content || "").toLowerCase().includes(query) ||
          (asset.tool_type || "").toLowerCase().includes(query) ||
          (asset.tool_label || "").toLowerCase().includes(query) ||
          (asset.tool_badge || "").toLowerCase().includes(query)
      );
    }

    if (sortBy === "newest") {
      result.sort(
        (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
      );
    } else if (sortBy === "oldest") {
      result.sort(
        (a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
      );
    } else if (sortBy === "topic") {
      result.sort((a, b) => (a.topic || a.title || "").localeCompare(b.topic || b.title || ""));
    }

    return result;
  }, [studioAssets, platformFilter, toolFilter, searchQuery, sortBy]);

  const groupedFolders = useMemo(() => {
    const folderMap = {};

    const makeKey = (value) => {
      const clean = (value || "").trim();
      return clean ? clean.toLowerCase() : "untitled";
    };

    const makeLabel = (value) => {
      const clean = (value || "").trim();
      return clean || t("libraryPage.labels.untitled");
    };

    filteredHooks.forEach((hook) => {
      const key = makeKey(hook.topic);
      if (!folderMap[key]) {
        folderMap[key] = {
          key,
          label: folderNames[key] || makeLabel(hook.topic),
          defaultLabel: makeLabel(hook.topic),
          hooks: [],
          assets: [],
          latestDate: hook.created_at || null
        };
      }
      folderMap[key].hooks.push(hook);

      if (
        hook.created_at &&
        (!folderMap[key].latestDate ||
          new Date(hook.created_at).getTime() > new Date(folderMap[key].latestDate).getTime())
      ) {
        folderMap[key].latestDate = hook.created_at;
      }
    });

    filteredStudioAssets.forEach((asset) => {
      const folderSource = asset.topic || asset.title;
      const key = makeKey(folderSource);

      if (!folderMap[key]) {
        folderMap[key] = {
          key,
          label: folderNames[key] || makeLabel(folderSource),
          defaultLabel: makeLabel(folderSource),
          hooks: [],
          assets: [],
          latestDate: asset.created_at || null
        };
      }
      folderMap[key].assets.push(asset);

      if (
        asset.created_at &&
        (!folderMap[key].latestDate ||
          new Date(asset.created_at).getTime() > new Date(folderMap[key].latestDate).getTime())
      ) {
        folderMap[key].latestDate = asset.created_at;
      }
    });

    return Object.values(folderMap).sort((a, b) => {
      if (sortBy === "topic") {
        return a.label.localeCompare(b.label);
      }

      const aTime = new Date(a.latestDate || 0).getTime();
      const bTime = new Date(b.latestDate || 0).getTime();

      if (sortBy === "oldest") return aTime - bTime;
      return bTime - aTime;
    });
  }, [filteredHooks, filteredStudioAssets, sortBy, folderNames, t]);

  useEffect(() => {
    setOpenFolders((prev) => {
      const next = { ...prev };
      groupedFolders.forEach((folder) => {
        if (typeof next[folder.key] === "undefined") {
          next[folder.key] = true;
        }
      });
      return next;
    });
  }, [groupedFolders]);

  const platforms = [
    "All",
    ...new Set(
      [...hooks.map((hook) => hook.platform), ...studioAssets.map((asset) => asset.platform).filter(Boolean)]
    )
  ];

  const availableStudioTools = useMemo(() => {
    const counts = {};

    studioAssets.forEach((asset) => {
      const normalized = normalizeToolType(asset.tool_type);
      counts[normalized] = (counts[normalized] || 0) + 1;
    });

    return Object.entries(counts)
      .map(([toolType, count]) => ({
        toolType,
        count,
        ...getToolMeta(toolType, null, null, t)
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [studioAssets, t]);

  const totalHooks = hooks.length;
  const favoriteCount = hooks.filter((hook) => hook.is_favorite).length;
  const averageScore = hooks.length
    ? hooks.reduce((sum, hook) => sum + hook.score, 0) / hooks.length
    : 0;

  const visibleHookIds = filteredHooks.map((hook) => hook.id);
  const allVisibleSelected =
    visibleHookIds.length > 0 && visibleHookIds.every((id) => selectedHookIds.includes(id));

  const toggleSelectHook = (hookId) => {
    if (!isCreatorOrHigher) {
      showUpgradePrompt(t("libraryPage.features.bulkSelection"));
      return;
    }

    setSelectedHookIds((prev) =>
      prev.includes(hookId) ? prev.filter((id) => id !== hookId) : [...prev, hookId]
    );
  };

  const toggleSelectAllVisible = () => {
    if (!isCreatorOrHigher) {
      showUpgradePrompt(t("libraryPage.features.bulkSelection"));
      return;
    }

    if (allVisibleSelected) {
      setSelectedHookIds((prev) => prev.filter((id) => !visibleHookIds.includes(id)));
      return;
    }

    setSelectedHookIds((prev) => [...new Set([...prev, ...visibleHookIds])]);
  };

  const clearSelection = () => {
    setSelectedHookIds([]);
  };

  const toggleFolder = (folderKey) => {
    setOpenFolders((prev) => ({
      ...prev,
      [folderKey]: !prev[folderKey]
    }));
  };

  const startRenameFolder = (folder) => {
    setEditingFolderKey(folder.key);
    setEditingFolderValue(folder.label);
    setError("");
  };

  const cancelRenameFolder = () => {
    setEditingFolderKey("");
    setEditingFolderValue("");
  };

  const saveRenameFolder = async (folder) => {
    const nextName = editingFolderValue.trim();
    if (!nextName) {
      setError(t("libraryPage.messages.folderNameRequired"));
      return;
    }

    try {
      setActionLoadingId(`rename-${folder.key}`);
      const response = await API.post("/library-folders/upsert", {
        folder_key: folder.key,
        display_name: nextName
      });

      setFolderNames((prev) => ({
        ...prev,
        [folder.key]: response?.data?.folder?.display_name || nextName
      }));

      setEditingFolderKey("");
      setEditingFolderValue("");
      setError("");
    } catch (err) {
      console.error("Failed to rename folder:", err);
      setError(err?.response?.data?.detail || t("libraryPage.messages.renameFolderFailed"));
    } finally {
      setActionLoadingId(null);
    }
  };

  const copyVisibleHooks = async () => {
    try {
      const text = filteredHooks
        .map(
          (hook, index) =>
            `${index + 1}. [${hook.platform}] ${hook.topic} (${t("libraryPage.labels.score")}: ${hook.score})\n${hook.content}`
        )
        .join("\n\n");

      await navigator.clipboard.writeText(text);
      setCopiedHookId("all-visible");
      setTimeout(() => setCopiedHookId(null), 2000);
    } catch (err) {
      console.error("Failed to copy visible hooks:", err);
      setError(t("libraryPage.messages.copyVisibleFailed"));
    }
  };

  const downloadFile = (filename, content, mimeType) => {
    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const exportVisibleHooksTxt = () => {
    const content = filteredHooks
      .map(
        (hook, index) =>
          `${index + 1}. ${t("libraryPage.export.topic")}: ${hook.topic}\n${t("libraryPage.export.platform")}: ${hook.platform}\n${t("libraryPage.export.score")}: ${hook.score}\n${t("libraryPage.export.favorite")}: ${
            hook.is_favorite ? t("libraryPage.export.yes") : t("libraryPage.export.no")
          }\n${t("libraryPage.export.created")}: ${hook.created_at || ""}\n${t("libraryPage.export.hook")}: ${hook.content}`
      )
      .join("\n\n-------------------------\n\n");

    downloadFile("hookora-hooks.txt", content, "text/plain;charset=utf-8");
  };

  const exportVisibleHooksCsv = async () => {
    if (!isCreatorOrHigher) {
      showUpgradePrompt(t("libraryPage.features.csvExport"));
      return;
    }

    try {
      const response = await API.post(
        "/hooks/export/csv",
        { hook_ids: filteredHooks.map((hook) => hook.id) },
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "hookora-hooks.csv";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed CSV export:", err);
      setError(err?.response?.data?.detail || t("libraryPage.messages.csvExportFailed"));
    }
  };

  const bulkDeleteSelected = async () => {
    if (!isCreatorOrHigher) {
      showUpgradePrompt(t("libraryPage.features.bulkDelete"));
      return;
    }

    if (!selectedHookIds.length) return;

    const shouldDelete = window.confirm(
      t("libraryPage.confirmations.deleteSelectedHooks", { count: selectedHookIds.length })
    );
    if (!shouldDelete) return;

    try {
      setBulkActionLoading(true);
      await API.post("/hooks/bulk/delete", { hook_ids: selectedHookIds });
      clearSelection();
      await loadLibrary();
    } catch (err) {
      console.error("Failed bulk delete:", err);
      setError(err?.response?.data?.detail || t("libraryPage.messages.bulkDeleteFailed"));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const bulkSetFavoriteState = async (makeFavorite) => {
    if (!isCreatorOrHigher) {
      showUpgradePrompt(
        makeFavorite ? t("libraryPage.features.bulkFavorite") : t("libraryPage.features.bulkUnfavorite")
      );
      return;
    }

    if (!selectedHookIds.length) return;

    try {
      setBulkActionLoading(true);
      const endpoint = makeFavorite ? "/hooks/bulk/favorite" : "/hooks/bulk/unfavorite";
      await API.post(endpoint, { hook_ids: selectedHookIds });
      await loadLibrary();
    } catch (err) {
      console.error("Failed bulk favorite update:", err);
      setError(err?.response?.data?.detail || t("libraryPage.messages.bulkUpdateFailed"));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const featureBadge = (label, color = "#7c3aed", bg = isDark ? "rgba(88,28,135,0.18)" : "#f5f3ff") => (
    <span
      style={{
        display: "inline-block",
        padding: "4px 8px",
        borderRadius: "999px",
        fontSize: "12px",
        fontWeight: "700",
        color,
        background: bg,
        marginLeft: "8px",
        border: isDark ? `1px solid ${colors.border}` : "none"
      }}
    >
      {label}
    </span>
  );

  const lockedButtonStyle = {
    padding: "10px 14px",
    borderRadius: "10px",
    border: `1px dashed ${colors.lockedBorder}`,
    background: colors.lockedBg,
    color: colors.lockedText,
    cursor: "pointer",
    fontWeight: "600"
  };

  if (loading) {
  return (
    <LibrarySkeleton
      isDark={isDark}
      isMobile={isMobile}
      colors={colors}
    />
  );
}

  if (error && !hooks.length && !studioAssets.length) {
    return (
      <div
        style={{
          padding: "40px",
          minHeight: "100vh",
          background: colors.pageBg,
          color: colors.softWarningText
        }}
      >
        <h2>{error}</h2>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: isMobile ? "24px 14px" : "32px 20px",
        background: colors.pageBg,
        backgroundImage: colors.pageGradient,
        minHeight: "100vh"
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <h1
          style={{
            marginBottom: "8px",
            color: colors.text,
            fontSize: isMobile ? "28px" : "34px"
          }}
        >
          {t("libraryPage.title")}
        </h1>
        <p style={{ color: colors.textMuted, marginTop: 0, lineHeight: "1.6" }}>
          {t("libraryPage.subtitle")}
        </p>

        <div
          style={{
            marginTop: "16px",
            marginBottom: "16px",
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: "18px",
            padding: "16px",
            boxShadow: colors.shadow,
            backdropFilter: isDark ? "blur(14px)" : "none"
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
              marginBottom: "8px"
            }}
          >
            <div style={{ fontWeight: "700", color: colors.text }}>
              {t("libraryPage.currentPlanLabel")}
            </div>
            <span
              style={{
                ...getPlanBadgeStyle(currentPlan),
                fontSize: "12px",
                fontWeight: "700",
                padding: "7px 11px",
                borderRadius: "999px"
              }}
            >
              {formatPlanLabel(currentPlan)}
            </span>
          </div>

          <div style={{ color: colors.textSoft, fontSize: "14px", lineHeight: "1.7" }}>
            {t("libraryPage.planBox.line1")}
            <br />
            {t("libraryPage.planBox.line2.before")} <strong>{t("libraryPage.planBox.line2.creator")}</strong> {t("libraryPage.planBox.line2.middle")} <strong>{t("libraryPage.planBox.line2.pro")}</strong>.
            <br />
            {t("libraryPage.planBox.line3.before")} <strong>{t("libraryPage.planBox.line3.pro")}</strong>.
          </div>
          {!isCreatorOrHigher && (
            <div style={{ marginTop: "12px" }}>
              <Link
                to="/pricing"
                style={{
                  display: "inline-block",
                  textDecoration: "none",
                  background: colors.linkButtonBg,
                  color: colors.linkButtonText,
                  padding: "10px 14px",
                  borderRadius: "10px",
                  fontWeight: "700"
                }}
              >
                {t("libraryPage.actions.upgradeToCreator")}
              </Link>
            </div>
          )}
        </div>

        {error && (
          <div
            style={{
              marginTop: "12px",
              marginBottom: "16px",
              background: colors.softWarningBg,
              border: `1px solid ${colors.softWarningBorder}`,
              borderRadius: "14px",
              padding: "14px 16px",
              color: colors.softWarningText,
              lineHeight: "1.6"
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "16px",
            marginTop: "24px"
          }}
        >
          <div style={statCardStyle}>
            <h3 style={{ marginTop: 0, color: colors.textSoft }}>{t("libraryPage.stats.savedHooks")}</h3>
            <p style={{ fontSize: "30px", fontWeight: "700", margin: "8px 0 0", color: colors.text }}>
              {totalHooks}
            </p>
          </div>

          <div style={statCardStyle}>
            <h3 style={{ marginTop: 0, color: colors.textSoft }}>{t("libraryPage.stats.savedStudioAssets")}</h3>
            <p style={{ fontSize: "30px", fontWeight: "700", margin: "8px 0 0", color: colors.text }}>
              {studioAssets.length}
            </p>
          </div>

          <div style={statCardStyle}>
            <h3 style={{ marginTop: 0, color: colors.textSoft }}>{t("libraryPage.stats.favoriteHooks")}</h3>
            <p style={{ fontSize: "30px", fontWeight: "700", margin: "8px 0 0", color: colors.text }}>
              {favoriteCount}
            </p>
          </div>

          <div style={statCardStyle}>
            <h3 style={{ marginTop: 0, color: colors.textSoft }}>
              {t("libraryPage.stats.averageHookScore")}
              {isPro ? featureBadge(t("libraryPage.badges.proInsights"), "#065f46", isDark ? "rgba(6,95,70,0.22)" : "#d1fae5") : null}
            </h3>
            <p style={{ fontSize: "30px", fontWeight: "700", margin: "8px 0 0", color: colors.text }}>
              {averageScore.toFixed(2)}
            </p>
          </div>
        </div>

        {availableStudioTools.length > 0 && (
          <div
            style={{
              marginTop: "18px",
              padding: "18px",
              border: `1px solid ${colors.border}`,
              borderRadius: "18px",
              background: colors.cardBg,
              boxShadow: colors.shadow,
              backdropFilter: isDark ? "blur(14px)" : "none"
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "12px",
                alignItems: "center",
                flexWrap: "wrap",
                marginBottom: "12px"
              }}
            >
              <h3 style={{ margin: 0, color: colors.text }}>{t("libraryPage.toolBreakdown.title")}</h3>
              <span style={{ color: colors.textMuted, fontSize: "14px" }}>
                {t("libraryPage.toolBreakdown.savedItemsCount", { count: studioAssets.length })}
              </span>
            </div>

            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
              {availableStudioTools.map((tool) => (
                <span
                  key={tool.toolType}
                  style={{
                    ...getBadgeStyle(tool.toolType),
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 12px",
                    borderRadius: "999px",
                    fontSize: "12px",
                    fontWeight: "700"
                  }}
                >
                  <span>{tool.label}</span>
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: "999px",
                      background: colors.accentSlateBg,
                      color: colors.accentSlateText,
                      border: `1px solid ${colors.accentSlateBorder}`
                    }}
                  >
                    {tool.count}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}

        <div
          style={{
            marginTop: "24px",
            padding: "20px",
            border: `1px solid ${colors.border}`,
            borderRadius: "18px",
            background: colors.cardBg,
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "14px",
            alignItems: "end",
            boxShadow: colors.shadow,
            backdropFilter: isDark ? "blur(14px)" : "none"
          }}
        >
          <div>
            <label style={{ fontWeight: "600", color: colors.textSoft, display: "block", marginBottom: "6px" }}>
              {t("libraryPage.filters.search")}
            </label>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t("libraryPage.filters.searchPlaceholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={{ fontWeight: "600", color: colors.textSoft, display: "block", marginBottom: "6px" }}>
              {t("libraryPage.filters.platform")}
            </label>
            <select
              value={platformFilter}
              onChange={(e) => setPlatformFilter(e.target.value)}
              style={inputStyle}
            >
              {platforms.map((platform) => (
                <option key={platform} value={platform}>
                  {platform === "All" ? t("libraryPage.filters.allPlatforms") : platform}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ fontWeight: "600", color: colors.textSoft, display: "block", marginBottom: "6px" }}>
              {t("libraryPage.filters.studioTool")}
            </label>
            <select
              value={toolFilter}
              onChange={(e) => setToolFilter(e.target.value)}
              style={inputStyle}
            >
              <option value="All">{t("libraryPage.filters.allStudioTools")}</option>
              {availableStudioTools.map((tool) => (
                <option key={tool.toolType} value={tool.toolType}>
                  {tool.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ fontWeight: "600", color: colors.textSoft, display: "block", marginBottom: "6px" }}>
              {t("libraryPage.filters.sortBy")}
            </label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={inputStyle}
            >
              <option value="newest">{t("libraryPage.sort.newest")}</option>
              <option value="oldest">{t("libraryPage.sort.oldest")}</option>
              <option value="score-high">{t("libraryPage.sort.highestScore")}</option>
              <option value="score-low">{t("libraryPage.sort.lowestScore")}</option>
              <option value="topic">{t("libraryPage.sort.topic")}</option>
            </select>
          </div>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: colors.textSoft,
              fontWeight: "600",
              minHeight: "42px"
            }}
          >
            <input
              type="checkbox"
              checked={favoritesOnly}
              onChange={(e) => setFavoritesOnly(e.target.checked)}
            />
            {t("libraryPage.filters.favoritesOnly")}
          </label>
        </div>

        <div
          style={{
            marginTop: "16px",
            padding: "20px",
            border: `1px solid ${colors.border}`,
            borderRadius: "18px",
            background: colors.cardBg,
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            alignItems: "center",
            boxShadow: colors.shadow,
            backdropFilter: isDark ? "blur(14px)" : "none"
          }}
        >
          {isCreatorOrHigher ? (
            <button
              onClick={toggleSelectAllVisible}
              style={{
                padding: "10px 14px",
                borderRadius: "10px",
                border: "none",
                background: colors.linkButtonBg,
                color: colors.linkButtonText,
                cursor: "pointer",
                fontWeight: "600",
                flex: isMobile ? "1 1 180px" : "0 0 auto"
              }}
            >
              {allVisibleSelected
                ? t("libraryPage.actions.unselectVisibleHooks")
                : t("libraryPage.actions.selectVisibleHooks")}
            </button>
          ) : (
            <button
              onClick={() => showUpgradePrompt(t("libraryPage.features.bulkSelection"))}
              style={{ ...lockedButtonStyle, flex: isMobile ? "1 1 180px" : "0 0 auto" }}
            >
              {t("libraryPage.actions.selectVisibleHooks")}
              {featureBadge(t("libraryPage.badges.creatorPlus"))}
            </button>
          )}

          {isCreatorOrHigher ? (
            <button
              onClick={clearSelection}
              disabled={!selectedHookIds.length}
              style={{
                padding: "10px 14px",
                borderRadius: "10px",
                border: "none",
                background: selectedHookIds.length ? "#64748b" : "#cbd5e1",
                color: "#fff",
                cursor: selectedHookIds.length ? "pointer" : "not-allowed",
                fontWeight: "600",
                flex: isMobile ? "1 1 180px" : "0 0 auto"
              }}
            >
              {t("libraryPage.actions.clearSelection")}
            </button>
          ) : (
            <button
              onClick={() => showUpgradePrompt(t("libraryPage.features.bulkSelection"))}
              style={{ ...lockedButtonStyle, flex: isMobile ? "1 1 180px" : "0 0 auto" }}
            >
              {t("libraryPage.actions.clearSelection")}
              {featureBadge(t("libraryPage.badges.creatorPlus"))}
            </button>
          )}

          <button
            onClick={copyVisibleHooks}
            disabled={!filteredHooks.length}
            style={{
              padding: "10px 14px",
              borderRadius: "10px",
              border: "none",
              background: filteredHooks.length ? "#2563eb" : "#cbd5e1",
              color: "#fff",
              cursor: filteredHooks.length ? "pointer" : "not-allowed",
              fontWeight: "600",
              flex: isMobile ? "1 1 180px" : "0 0 auto"
            }}
          >
            {copiedHookId === "all-visible"
              ? t("libraryPage.actions.copiedAll")
              : t("libraryPage.actions.copyVisibleHooks")}
          </button>

          <button
            onClick={exportVisibleHooksTxt}
            disabled={!filteredHooks.length}
            style={{
              padding: "10px 14px",
              borderRadius: "10px",
              border: "none",
              background: filteredHooks.length ? "#16a34a" : "#cbd5e1",
              color: "#fff",
              cursor: filteredHooks.length ? "pointer" : "not-allowed",
              fontWeight: "600",
              flex: isMobile ? "1 1 180px" : "0 0 auto"
            }}
          >
            {t("libraryPage.actions.exportHooksTxt")}
          </button>

          {isCreatorOrHigher ? (
            <button
              onClick={exportVisibleHooksCsv}
              disabled={!filteredHooks.length}
              style={{
                padding: "10px 14px",
                borderRadius: "10px",
                border: "none",
                background: filteredHooks.length ? "#0f766e" : "#cbd5e1",
                color: "#fff",
                cursor: filteredHooks.length ? "pointer" : "not-allowed",
                fontWeight: "600",
                flex: isMobile ? "1 1 180px" : "0 0 auto"
              }}
            >
              {t("libraryPage.actions.exportHooksCsv")}
            </button>
          ) : (
            <button
              onClick={() => showUpgradePrompt(t("libraryPage.features.csvExport"))}
              style={{ ...lockedButtonStyle, flex: isMobile ? "1 1 180px" : "0 0 auto" }}
            >
              {t("libraryPage.actions.exportHooksCsv")}
              {featureBadge(t("libraryPage.badges.creatorPlus"))}
            </button>
          )}

          {isCreatorOrHigher ? (
            <button
              onClick={() => bulkSetFavoriteState(true)}
              disabled={!selectedHookIds.length || bulkActionLoading}
              style={{
                padding: "10px 14px",
                borderRadius: "10px",
                border: "none",
                background:
                  selectedHookIds.length && !bulkActionLoading ? "#f59e0b" : "#cbd5e1",
                color: "#fff",
                cursor:
                  selectedHookIds.length && !bulkActionLoading ? "pointer" : "not-allowed",
                fontWeight: "600",
                flex: isMobile ? "1 1 180px" : "0 0 auto"
              }}
            >
              {t("libraryPage.actions.favoriteSelected")}
            </button>
          ) : (
            <button
              onClick={() => showUpgradePrompt(t("libraryPage.features.bulkFavorite"))}
              style={{ ...lockedButtonStyle, flex: isMobile ? "1 1 180px" : "0 0 auto" }}
            >
              {t("libraryPage.actions.favoriteSelected")}
              {featureBadge(t("libraryPage.badges.creatorPlus"))}
            </button>
          )}

          {isCreatorOrHigher ? (
            <button
              onClick={() => bulkSetFavoriteState(false)}
              disabled={!selectedHookIds.length || bulkActionLoading}
              style={{
                padding: "10px 14px",
                borderRadius: "10px",
                border: "none",
                background:
                  selectedHookIds.length && !bulkActionLoading ? "#f97316" : "#cbd5e1",
                color: "#fff",
                cursor:
                  selectedHookIds.length && !bulkActionLoading ? "pointer" : "not-allowed",
                fontWeight: "600",
                flex: isMobile ? "1 1 180px" : "0 0 auto"
              }}
            >
              {t("libraryPage.actions.unfavoriteSelected")}
            </button>
          ) : (
            <button
              onClick={() => showUpgradePrompt(t("libraryPage.features.bulkUnfavorite"))}
              style={{ ...lockedButtonStyle, flex: isMobile ? "1 1 180px" : "0 0 auto" }}
            >
              {t("libraryPage.actions.unfavoriteSelected")}
              {featureBadge(t("libraryPage.badges.creatorPlus"))}
            </button>
          )}

          {isCreatorOrHigher ? (
            <button
              onClick={bulkDeleteSelected}
              disabled={!selectedHookIds.length || bulkActionLoading}
              style={{
                padding: "10px 14px",
                borderRadius: "10px",
                border: "none",
                background:
                  selectedHookIds.length && !bulkActionLoading ? "#ef4444" : "#cbd5e1",
                color: "#fff",
                cursor:
                  selectedHookIds.length && !bulkActionLoading ? "pointer" : "not-allowed",
                fontWeight: "600",
                flex: isMobile ? "1 1 180px" : "0 0 auto"
              }}
            >
              {bulkActionLoading ? t("libraryPage.states.working") : t("libraryPage.actions.deleteSelected")}
            </button>
          ) : (
            <button
              onClick={() => showUpgradePrompt(t("libraryPage.features.bulkDelete"))}
              style={{ ...lockedButtonStyle, flex: isMobile ? "1 1 180px" : "0 0 auto" }}
            >
              {t("libraryPage.actions.deleteSelected")}
              {featureBadge(t("libraryPage.badges.creatorPlus"))}
            </button>
          )}

          <span style={{ color: colors.chipText, fontSize: "14px", fontWeight: "600" }}>
            {t("libraryPage.labels.selectedHooksCount", { count: selectedHookIds.length })}
          </span>
        </div>

        {!isCreatorOrHigher && (
          <div
            style={{
              marginTop: "14px",
              background: colors.warningBg,
              border: `1px solid ${colors.warningBorder}`,
              borderRadius: "14px",
              padding: "14px 16px",
              color: colors.warningText,
              lineHeight: "1.6"
            }}
          >
            {t("libraryPage.notices.freeLock.before")}{" "}
            <Link to="/pricing" style={{ color: colors.warningText, fontWeight: "700" }}>
              {t("libraryPage.notices.freeLock.upgradeLink")}
            </Link>
            .
          </div>
        )}

        {!isPro && (
          <div
            style={{
              marginTop: "14px",
              background: colors.purpleBg,
              border: `1px solid ${colors.purpleBorder}`,
              borderRadius: "14px",
              padding: "14px 16px",
              color: colors.purpleText,
              lineHeight: "1.6"
            }}
          >
            {t("libraryPage.notices.proPreview.before")} <strong>{t("libraryPage.notices.proPreview.pro")}</strong>.
          </div>
        )}

        <div style={{ marginTop: "28px" }}>
          <h2 style={{ color: colors.text }}>{t("libraryPage.folders.title")}</h2>

          {groupedFolders.length ? (
            <div style={{ display: "grid", gap: "16px" }}>
              {groupedFolders.map((folder) => {
                const isOpen = openFolders[folder.key];
                const totalItems = folder.hooks.length + folder.assets.length;
                const isEditing = editingFolderKey === folder.key;

                return (
                  <div
                    key={folder.key}
                    style={{
                      border: `1px solid ${colors.border}`,
                      borderRadius: "18px",
                      background: colors.folderBg,
                      boxShadow: colors.shadowSoft,
                      overflow: "hidden"
                    }}
                  >
                    <div
                      style={{
                        padding: isMobile ? "14px" : "16px 18px",
                        background: colors.folderHeaderBg,
                        borderBottom: isOpen ? `1px solid ${colors.border}` : "none",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: "12px",
                        flexWrap: "wrap"
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {isEditing ? (
                          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                            <input
                              type="text"
                              value={editingFolderValue}
                              onChange={(e) => setEditingFolderValue(e.target.value)}
                              style={{
                                ...inputStyle,
                                flex: 1,
                                minWidth: isMobile ? "100%" : "240px"
                              }}
                              placeholder={t("libraryPage.folders.folderNamePlaceholder")}
                            />
                            <button
                              onClick={() => saveRenameFolder(folder)}
                              disabled={actionLoadingId === `rename-${folder.key}`}
                              style={{
                                padding: "10px 14px",
                                borderRadius: "10px",
                                border: "none",
                                background: "#2563eb",
                                color: "#fff",
                                cursor:
                                  actionLoadingId === `rename-${folder.key}` ? "not-allowed" : "pointer",
                                fontWeight: "700",
                                opacity: actionLoadingId === `rename-${folder.key}` ? 0.75 : 1
                              }}
                            >
                              {actionLoadingId === `rename-${folder.key}` ? t("libraryPage.actions.saving") : t("libraryPage.actions.save")}
                            </button>
                            <button
                              onClick={cancelRenameFolder}
                              style={{
                                padding: "10px 14px",
                                borderRadius: "10px",
                                border: "none",
                                background: "#64748b",
                                color: "#fff",
                                cursor: "pointer",
                                fontWeight: "700"
                              }}
                            >
                              {t("libraryPage.actions.cancel")}
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => toggleFolder(folder.key)}
                            style={{
                              background: "transparent",
                              border: "none",
                              padding: 0,
                              margin: 0,
                              cursor: "pointer",
                              textAlign: "left",
                              color: colors.text,
                              width: "100%"
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                              <span style={{ fontSize: "20px" }}>{isOpen ? "📂" : "📁"}</span>
                              <span style={{ fontWeight: "800", fontSize: "16px" }}>{folder.label}</span>
                              <span
                                style={{
                                  background: colors.chipBg,
                                  color: colors.chipText,
                                  padding: "4px 10px",
                                  borderRadius: "999px",
                                  fontSize: "12px",
                                  fontWeight: "700"
                                }}
                              >
                                {t("libraryPage.folders.itemsCount", { count: totalItems })}
                              </span>
                              {folder.hooks.length > 0 && (
                                <span
                                  style={{
                                    background: colors.favoriteBg,
                                    color: colors.favoriteText,
                                    padding: "4px 10px",
                                    borderRadius: "999px",
                                    fontSize: "12px",
                                    fontWeight: "700"
                                  }}
                                >
                                  {t("libraryPage.folders.hooksCount", { count: folder.hooks.length })}
                                </span>
                              )}
                              {folder.assets.length > 0 && (
                                <span
                                  style={{
                                    background: colors.chipBg,
                                    color: colors.chipText,
                                    padding: "4px 10px",
                                    borderRadius: "999px",
                                    fontSize: "12px",
                                    fontWeight: "700"
                                  }}
                                >
                                  {t("libraryPage.folders.assetsCount", { count: folder.assets.length })}
                                </span>
                              )}
                            </div>
                          </button>
                        )}
                      </div>

                      {!isEditing && (
                        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                          <button
                            onClick={() => startRenameFolder(folder)}
                            style={{
                              padding: "10px 14px",
                              borderRadius: "10px",
                              border: "none",
                              background: "#0f766e",
                              color: "#fff",
                              cursor: "pointer",
                              fontWeight: "700"
                            }}
                          >
                            {t("libraryPage.actions.rename")}
                          </button>

                          <button
                            onClick={() => deleteFolder(folder.key, folder)}
                            disabled={actionLoadingId === `folder-${folder.key}`}
                            style={{
                              padding: "10px 14px",
                              borderRadius: "10px",
                              border: "none",
                              background: "#ef4444",
                              color: "#fff",
                              cursor:
                                actionLoadingId === `folder-${folder.key}` ? "not-allowed" : "pointer",
                              fontWeight: "700",
                              opacity: actionLoadingId === `folder-${folder.key}` ? 0.75 : 1
                            }}
                          >
                            {actionLoadingId === `folder-${folder.key}` ? t("libraryPage.actions.deleting") : t("libraryPage.actions.deleteFolder")}
                          </button>
                        </div>
                      )}
                    </div>

                    {isOpen && !isEditing && (
                      <div style={{ padding: isMobile ? "14px" : "18px" }}>
                        {folder.assets.length > 0 && (
                          <div style={{ marginBottom: folder.hooks.length ? "20px" : 0 }}>
                            <h3 style={{ color: colors.text, marginTop: 0 }}>{t("libraryPage.sections.studioAssets")}</h3>
                            <ul style={{ paddingLeft: 0, margin: 0 }}>
                              {folder.assets.map((asset) => (
                                <li
                                  key={asset.id}
                                  style={{
                                    marginBottom: "16px",
                                    border: `1px solid ${colors.border}`,
                                    padding: "16px",
                                    borderRadius: "16px",
                                    listStyle: "none",
                                    background: colors.cardBgStrong,
                                    boxShadow: colors.shadowSoft
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      gap: "12px",
                                      flexWrap: "wrap",
                                      alignItems: "flex-start"
                                    }}
                                  >
                                    <div style={{ color: colors.text, minWidth: 0, flex: 1 }}>
                                      <strong>{asset.title}</strong>

                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "8px",
                                          flexWrap: "wrap",
                                          alignItems: "center",
                                          marginTop: "8px"
                                        }}
                                      >
                                        <span
                                          style={{
                                            ...getBadgeStyle(asset.tool_type),
                                            display: "inline-block",
                                            padding: "5px 10px",
                                            borderRadius: "999px",
                                            fontSize: "12px",
                                            fontWeight: "700"
                                          }}
                                        >
                                          {asset.tool_badge}
                                        </span>

                                        <span
                                          style={{
                                            background: colors.chipBg,
                                            color: colors.chipText,
                                            padding: "5px 10px",
                                            borderRadius: "999px",
                                            fontSize: "12px",
                                            fontWeight: "600"
                                          }}
                                        >
                                          {asset.tool_label}
                                        </span>

                                        {asset.platform ? (
                                          <span
                                            style={{
                                              background: colors.accentSlateBg,
                                              color: colors.accentSlateText,
                                              border: `1px solid ${colors.accentSlateBorder}`,
                                              padding: "5px 10px",
                                              borderRadius: "999px",
                                              fontSize: "12px",
                                              fontWeight: "600"
                                            }}
                                          >
                                            {asset.platform}
                                          </span>
                                        ) : null}
                                      </div>

                                      <div
                                        style={{
                                          color: colors.textMuted,
                                          fontSize: "14px",
                                          marginTop: "8px",
                                          lineHeight: "1.5"
                                        }}
                                      >
                                        {t("libraryPage.labels.toolType")}: {asset.tool_type}
                                      </div>
                                    </div>

                                    <span
                                      style={{
                                        background: colors.chipBg,
                                        color: colors.chipText,
                                        padding: "5px 10px",
                                        borderRadius: "999px",
                                        fontSize: "12px",
                                        fontWeight: "500"
                                      }}
                                    >
                                      {asset.created_at
                                        ? new Date(asset.created_at).toLocaleString()
                                        : t("libraryPage.labels.noDate")}
                                    </span>
                                  </div>

                                  <div
                                    style={{
                                      marginTop: "10px",
                                      color: colors.textSoft,
                                      lineHeight: "1.7",
                                      whiteSpace: "pre-wrap"
                                    }}
                                  >
                                    {asset.content}
                                  </div>

                                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "14px" }}>
                                    <button
                                      onClick={() => copyHook(asset.content, `asset-copy-${asset.id}`)}
                                      style={{
                                        padding: "10px 14px",
                                        borderRadius: "10px",
                                        border: "none",
                                        backgroundColor:
                                          copiedHookId === `asset-copy-${asset.id}` ? "#16a34a" : "#2563eb",
                                        color: "#fff",
                                        cursor: "pointer",
                                        fontWeight: "600"
                                      }}
                                    >
                                      {copiedHookId === `asset-copy-${asset.id}` ? t("libraryPage.actions.copied") : t("libraryPage.actions.copy")}
                                    </button>

                                    <button
                                      onClick={() => deleteStudioAsset(asset.id)}
                                      disabled={actionLoadingId === `asset-${asset.id}`}
                                      style={{
                                        padding: "10px 14px",
                                        borderRadius: "10px",
                                        border: "none",
                                        backgroundColor: "#ef4444",
                                        color: "#fff",
                                        cursor: actionLoadingId === `asset-${asset.id}` ? "not-allowed" : "pointer",
                                        fontWeight: "600",
                                        opacity: actionLoadingId === `asset-${asset.id}` ? 0.75 : 1
                                      }}
                                    >
                                      {actionLoadingId === `asset-${asset.id}` ? t("libraryPage.states.working") : t("libraryPage.actions.delete")}
                                    </button>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {folder.hooks.length > 0 && (
                          <div>
                            <h3 style={{ color: colors.text, marginTop: 0 }}>{t("libraryPage.sections.hooks")}</h3>
                            <ul style={{ paddingLeft: 0, margin: 0 }}>
                              {folder.hooks.map((hook) => (
                                <li
                                  key={hook.id}
                                  style={{
                                    marginBottom: "16px",
                                    border: `1px solid ${colors.border}`,
                                    padding: "16px",
                                    borderRadius: "16px",
                                    listStyle: "none",
                                    background: colors.cardBgStrong,
                                    boxShadow: colors.shadowSoft
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      gap: "12px",
                                      flexWrap: "wrap",
                                      alignItems: "flex-start"
                                    }}
                                  >
                                    <div style={{ display: "flex", gap: "10px", alignItems: "flex-start", minWidth: 0 }}>
                                      {isCreatorOrHigher ? (
                                        <input
                                          type="checkbox"
                                          checked={selectedHookIds.includes(hook.id)}
                                          onChange={() => toggleSelectHook(hook.id)}
                                          style={{ marginTop: "4px" }}
                                        />
                                      ) : (
                                        <button
                                          onClick={() => showUpgradePrompt(t("libraryPage.features.bulkSelection"))}
                                          style={{
                                            marginTop: "2px",
                                            border: `1px dashed ${colors.lockedBorder}`,
                                            background: colors.lockedBg,
                                            color: colors.lockedText,
                                            borderRadius: "8px",
                                            padding: "4px 8px",
                                            cursor: "pointer",
                                            fontSize: "12px",
                                            fontWeight: "700",
                                            flexShrink: 0
                                          }}
                                        >
                                          {t("libraryPage.badges.locked")}
                                        </button>
                                      )}

                                      <div style={{ color: colors.text, minWidth: 0 }}>
                                        <strong>{hook.topic}</strong> ({hook.platform}) - {t("libraryPage.labels.score")}: {hook.score}
                                      </div>
                                    </div>

                                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                      {hook.is_favorite && (
                                        <span
                                          style={{
                                            background: colors.favoriteBg,
                                            color: colors.favoriteText,
                                            padding: "5px 10px",
                                            borderRadius: "999px",
                                            fontSize: "12px",
                                            fontWeight: "600"
                                          }}
                                        >
                                          {t("libraryPage.labels.favorite")}
                                        </span>
                                      )}

                                      <span
                                        style={{
                                          background: colors.chipBg,
                                          color: colors.chipText,
                                          padding: "5px 10px",
                                          borderRadius: "999px",
                                          fontSize: "12px",
                                          fontWeight: "500"
                                        }}
                                      >
                                        {hook.created_at
                                          ? new Date(hook.created_at).toLocaleString()
                                          : t("libraryPage.labels.noDate")}
                                      </span>
                                    </div>
                                  </div>

                                  <p style={{ marginTop: "10px", marginBottom: "8px", color: colors.textSoft, lineHeight: "1.6" }}>
                                    {hook.content}
                                  </p>

                                  {renderScoreBar(hook.score)}

                                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginTop: "14px" }}>
                                    <button
                                      onClick={() => copyHook(hook.content, hook.id)}
                                      style={{
                                        padding: "10px 14px",
                                        borderRadius: "10px",
                                        border: "none",
                                        backgroundColor: copiedHookId === hook.id ? "#16a34a" : "#2563eb",
                                        color: "#fff",
                                        cursor: "pointer",
                                        fontWeight: "600"
                                      }}
                                    >
                                      {copiedHookId === hook.id ? t("libraryPage.actions.copied") : t("libraryPage.actions.copyHook")}
                                    </button>

                                    <button
                                      onClick={() => regenerateHook(hook.id)}
                                      disabled={actionLoadingId === hook.id}
                                      style={{
                                        padding: "10px 14px",
                                        borderRadius: "10px",
                                        border: "none",
                                        backgroundColor: "#7c3aed",
                                        color: "#fff",
                                        cursor: actionLoadingId === hook.id ? "not-allowed" : "pointer",
                                        fontWeight: "600",
                                        opacity: actionLoadingId === hook.id ? 0.75 : 1
                                      }}
                                    >
                                      {actionLoadingId === hook.id ? t("libraryPage.states.working") : t("libraryPage.actions.regenerate")}
                                    </button>

                                    <button
                                      onClick={() => toggleFavorite(hook.id)}
                                      disabled={actionLoadingId === hook.id}
                                      style={{
                                        padding: "10px 14px",
                                        borderRadius: "10px",
                                        border: "none",
                                        backgroundColor: hook.is_favorite ? "#f59e0b" : "#64748b",
                                        color: "#fff",
                                        cursor: actionLoadingId === hook.id ? "not-allowed" : "pointer",
                                        fontWeight: "600",
                                        opacity: actionLoadingId === hook.id ? 0.75 : 1
                                      }}
                                    >
                                      {actionLoadingId === hook.id
                                        ? t("libraryPage.actions.saving")
                                        : hook.is_favorite
                                          ? t("libraryPage.actions.unfavorite")
                                          : t("libraryPage.actions.favorite")}
                                    </button>

                                    <button
                                      onClick={() => deleteHook(hook.id)}
                                      disabled={actionLoadingId === hook.id}
                                      style={{
                                        padding: "10px 14px",
                                        borderRadius: "10px",
                                        border: "none",
                                        backgroundColor: "#ef4444",
                                        color: "#fff",
                                        cursor: actionLoadingId === hook.id ? "not-allowed" : "pointer",
                                        fontWeight: "600",
                                        opacity: actionLoadingId === hook.id ? 0.75 : 1
                                      }}
                                    >
                                      {actionLoadingId === hook.id ? t("libraryPage.states.working") : t("libraryPage.actions.delete")}
                                    </button>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div
              style={{
                marginTop: "16px",
                padding: "24px",
                borderRadius: "16px",
                background: colors.cardBg,
                border: `1px solid ${colors.border}`,
                color: colors.textSoft,
                boxShadow: colors.shadow
              }}
            >
              {t("libraryPage.states.noFoldersMatch")}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HookLibrary;